import { ApiService } from "./../../../shared/services/api/api.service";

import { Component, effect, inject, type OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { GloControlComponent } from "../../../shared/components/glo-control/glo-control.component";
import { GloPaginationComponent } from "../../../shared/components/pagination/pagination.component";
import { GloButtonComponent } from "../../../shared/components/glo-button/glo-button.component";
import Swal from "sweetalert2";
import { Employee } from "../../../shared/models/employee.model";
import { Salon } from "../../../shared/models/salon.model";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { RouterModule } from "@angular/router";
import { environment } from "../../../../environments/environment";
import { AddEmployeeComponent } from "../add-employee/add-employee.component";
import { BusinessSalonContextService } from "../../../shared/services/api/business-salon-context.service";

@Component({
  selector: "business-employee-management",
  imports: [
    CommonModule,
    FormsModule,
    GloControlComponent,
    GloButtonComponent,
    LoaderComponent,
    RouterModule,
    GloPaginationComponent,
  ],
  templateUrl: "./employee-management.component.html",
  styleUrls: ["./employee-management.component.scss"],
  standalone: true,
})
export class BusinessEmployeeManagementComponent implements OnInit {
  private dialog = inject(MatDialog);

  readonly salonContext = inject(BusinessSalonContextService);

  assetUrl = environment.assetBaseUrl;

  employees: Employee[] = [];
  filteredEmployees: Employee[] = [];
  viewEmployees: Employee[] = [];

  selectedEmployee: Employee | null = null;
  selectedStatusFilter: "all" | "active" | "inactive" = "all";
  selectedSalonFilter = "all";
  searchTerm = "";

  currentPage = 1;
  itemsPerPage = 10;

  employeeCount = 0;

  /*
   * These are the salons currently selected in the global sidebar context.
   * The local salon dropdown filters only within this list.
   */
  salons: Salon[] = [];

  loading = false;

  /*
   * Prevent stale async employee responses from overwriting a newer
   * sidebar salon selection.
   */
  private loadVersion = 0;

  constructor(private apiService: ApiService) {
    effect(() => {
      const allSalons = this.salonContext.salons();
      const selectedSalonIds = this.salonContext.selectedSalonIds();

      if (!allSalons.length) {
        return;
      }

      this.syncSelectedSalons(allSalons, selectedSalonIds);

      this.syncLocalSalonFilter();

      this.loadAllEmployees();
    });
  }

  ngOnInit(): void {
    /*
     * Initial data loading is handled by the salon-context effect.
     */
  }

  /* =========================================================
     KPI helpers
     ========================================================= */

  get activeEmployees(): number {
    return this.employees.filter((employee: any) => employee.is_active).length;
  }

  get inactiveEmployees(): number {
    return this.employees.filter((employee: any) => !employee.is_active).length;
  }

  get activeEmployeePercent(): number {
    if (!this.employeeCount) {
      return 0;
    }

    return Math.round((this.activeEmployees / this.employeeCount) * 100);
  }

  get salonsCovered(): number {
    const salonIds = new Set(
      this.employees
        .map((employee: any) =>
          String(employee.salon ?? employee.salon_id ?? ""),
        )
        .filter(Boolean),
    );

    return salonIds.size;
  }

  get departmentCount(): number {
    const departments = new Set(
      this.employees
        .map((employee: any) => this.getEmployeeDepartment(employee))
        .filter((value) => value && value !== "N/A"),
    );

    return departments.size;
  }

  /* =========================================================
     Salon context
     ========================================================= */

  private syncSelectedSalons(
    allSalons: any[],
    selectedSalonIds: string[],
  ): void {
    const selectedSet = new Set(selectedSalonIds.map(String));

    if (!selectedSet.size) {
      this.salons = [];
      return;
    }

    this.salons = allSalons
      .filter((salon: any) => selectedSet.has(String(salon.salon_id)))
      .map(
        (salon: any) =>
          ({
            ...salon,
          }) as Salon,
      )
      .sort((a: any, b: any) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      );
  }

  private syncLocalSalonFilter(): void {
    if (this.selectedSalonFilter === "all") {
      return;
    }

    const availableSalonIds = new Set(
      this.salons.map((salon: any) => String(salon.salon_id)),
    );

    if (!availableSalonIds.has(String(this.selectedSalonFilter))) {
      this.selectedSalonFilter = "all";
    }
  }

  /* =========================================================
     Employee display helpers
     ========================================================= */

  resolveEmployeeId(employee: any): string | null {
    return (
      employee?.resolved_user_id ||
      employee?.user_id ||
      employee?.id ||
      employee?.user ||
      null
    );
  }

  resolveEmployeeFullName(employee: any): string {
    return (
      employee?.full_name ||
      employee?.user_full_name ||
      employee?.email ||
      employee?.user_email ||
      "this employee"
    );
  }

  getEmployeeEmail(employee: any): string {
    return employee?.email || employee?.user_email || "No email";
  }

  getEmployeePhone(employee: any): string {
    return employee?.phone_number || "No phone";
  }

  getEmployeeSalonName(employee: any): string {
    return (
      employee?.salon_name ||
      this.getSalonName(employee?.salon || employee?.salon_id) ||
      "N/A"
    );
  }

  getEmployeeRole(employee: any): string {
    return employee?.job_title || "N/A";
  }

  getEmployeeDepartment(employee: any): string {
    return employee?.departmentName || employee?.department_name || "N/A";
  }

  getEmployeeReportingTo(employee: any): string {
    return employee?.reportingTo || employee?.reporting_to || "N/A";
  }

  getEmployeeWorkingDays(employee: any): string {
    return employee?.workingDays || employee?.working_days || "N/A";
  }

  getInitials(employee: any): string {
    const name = this.resolveEmployeeFullName(employee);

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part.charAt(0))
      .join("")
      .toUpperCase();
  }

  selectEmployee(employee: Employee): void {
    this.selectedEmployee = employee;
  }

  /* =========================================================
     Load employees for selected salons
     ========================================================= */

  loadAllEmployees(): void {
    const version = ++this.loadVersion;

    this.loading = true;

    /*
     * No salons selected globally = no employees in scope.
     */
    if (!this.salons.length) {
      this.clearEmployees();
      this.loading = false;
      return;
    }

    const employeeRequests = this.salons.map((salon: any) =>
      this.apiService
        .request(
          API_ENDPOINTS.staff.listAcrossSalon(String(salon.salon_id)),
          "GET",
        )
        .toPromise()
        .then((response: any) => {
          const results = Array.isArray(response)
            ? response
            : (response?.results ??
              response?.data?.results ??
              response?.data ??
              []);

          const employees = Array.isArray(results) ? results : [];

          return employees.map((employee: any) => ({
            ...employee,

            salon: employee?.salon ?? salon.salon_id,

            salon_id: employee?.salon_id ?? employee?.salon ?? salon.salon_id,

            salon_name: employee?.salon_name ?? salon.name,

            resolved_user_id:
              employee?.user_id ?? employee?.id ?? employee?.user ?? null,
          }));
        })
        .catch((error) => {
          console.error(
            "Error loading employees for salon:",
            salon.name,
            error,
          );

          return [];
        }),
    );

    Promise.all(employeeRequests)
      .then((allEmployees) => {
        if (version !== this.loadVersion) {
          return;
        }

        /*
         * A staff member should normally belong to one salon row, but use a
         * composite key so accidental duplicate API data does not inflate KPIs.
         */
        const uniqueEmployees = new Map<string, any>();

        allEmployees.flat().forEach((employee: any) => {
          const employeeId = this.resolveEmployeeId(employee) || "unknown";

          const salonId = String(employee.salon_id ?? employee.salon ?? "");

          const key = `${employeeId}:${salonId}`;

          uniqueEmployees.set(key, employee);
        });

        this.employees = Array.from(uniqueEmployees.values());

        this.employeeCount = this.employees.length;

        this.currentPage = 1;

        /*
         * Preserve search/status/local salon filter when the global
         * sidebar salon scope changes.
         */
        this.applyFilters(false);

        this.loading = false;
      })
      .catch((error) => {
        if (version !== this.loadVersion) {
          return;
        }

        console.error("Error loading employees:", error);

        this.clearEmployees();

        Swal.fire({
          title: "Error loading employees!",
          text: "Something went wrong, please try again.",
          icon: "error",
          timer: 5000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });

        this.loading = false;
      });
  }

  private clearEmployees(): void {
    this.employees = [];
    this.filteredEmployees = [];
    this.viewEmployees = [];
    this.employeeCount = 0;
    this.selectedEmployee = null;
    this.currentPage = 1;
  }

  /*
   * Kept for compatibility with any existing code that may still call this
   * method. The page-level salon dropdown is now a local filter, so employee
   * data does not need another API reload.
   */
  loadEmployees(): void {
    this.applyFilters();
  }

  /* =========================================================
     Add / Edit / Deactivate
     ========================================================= */

  openEmployeeAdd(): void {
    const dialogRef = this.dialog.open(AddEmployeeComponent, {
      panelClass: ["crm-detail-dialog"],

      data: {
        isEdit: false,
        employee: null,

        /*
         * Allows the add form to use the current global scope if it
         * supports these values.
         */
        selectedSalonIds: this.salonContext.selectedSalonIds(),
      },

      width: "760px",

      maxWidth: "100vw",

      height: "100vh",

      position: {
        right: "0",
        top: "0",
      },

      autoFocus: false,

      restoreFocus: false,
    });

    dialogRef.afterClosed().subscribe((refresh) => {
      if (refresh !== false) {
        this.loadAllEmployees();
      }
    });
  }

  editEmployee(employee: any): void {
    const employeeId = this.resolveEmployeeId(employee);

    if (!employeeId) {
      console.error("No employee id found for navigation", employee);

      Swal.fire({
        title: "Unable to edit employee",
        text: "Employee id is missing from the record.",
        icon: "error",
        timer: 4000,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });

      return;
    }

    const dialogRef = this.dialog.open(AddEmployeeComponent, {
      panelClass: ["crm-detail-dialog"],

      data: {
        isEdit: true,

        employee,

        employee_id: employeeId,

        selectedSalonIds: this.salonContext.selectedSalonIds(),
      },

      width: "760px",

      maxWidth: "100vw",

      height: "100vh",

      position: {
        right: "0",
        top: "0",
      },

      autoFocus: false,

      restoreFocus: false,
    });

    dialogRef.afterClosed().subscribe((refresh) => {
      if (refresh !== false) {
        this.loadAllEmployees();
      }
    });
  }

  deleteEmployee(employee: any): void {
    const employeeId = this.resolveEmployeeId(employee);

    const employeeName = this.resolveEmployeeFullName(employee);

    if (!employeeId) {
      console.error("No employee id found for delete", employee);

      Swal.fire({
        title: "Unable to delete employee",
        text: "Employee id is missing from the record.",
        icon: "error",
        timer: 4000,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });

      return;
    }

    if (!confirm(`Are you sure you want to deactivate ${employeeName}?`)) {
      return;
    }

    this.apiService
      .request(
        API_ENDPOINTS.staff.updateEmployee(employeeId),
        "PATCH",
        {
          is_active: false,
        },
        "Employee Deactivated Successfully!",
      )
      .subscribe({
        next: () => {
          this.loadAllEmployees();
        },

        error: (error) => {
          console.error("Error deactivating employee:", error);
        },
      });
  }

  /* =========================================================
     Filters
     ========================================================= */

  getSalonName(salonId: string): string {
    const id = String(salonId || "");

    const salon = this.salons.find((item: any) => String(item.salon_id) === id);

    return salon?.name || "Unknown Salon";
  }

  onSearch(searchValue: string): void {
    this.searchTerm = searchValue || "";

    this.applyFilters();
  }

  applyFilters(resetPage = true): void {
    const term = this.searchTerm.toLowerCase().trim();

    let results = [...this.employees];

    if (term) {
      results = results.filter((employee: any) => {
        const fullName = this.resolveEmployeeFullName(employee).toLowerCase();

        const email = this.getEmployeeEmail(employee).toLowerCase();

        const phone = this.getEmployeePhone(employee).toLowerCase();

        const salon = this.getEmployeeSalonName(employee).toLowerCase();

        const role = this.getEmployeeRole(employee).toLowerCase();

        const department = this.getEmployeeDepartment(employee).toLowerCase();

        return (
          fullName.includes(term) ||
          email.includes(term) ||
          phone.includes(term) ||
          salon.includes(term) ||
          role.includes(term) ||
          department.includes(term)
        );
      });
    }

    if (this.selectedStatusFilter === "active") {
      results = results.filter((employee: any) => employee.is_active);
    }

    if (this.selectedStatusFilter === "inactive") {
      results = results.filter((employee: any) => !employee.is_active);
    }

    if (this.selectedSalonFilter !== "all") {
      const selectedId = String(this.selectedSalonFilter);

      results = results.filter(
        (employee: any) =>
          String(employee.salon ?? employee.salon_id ?? "") === selectedId,
      );
    }

    this.filteredEmployees = results;

    if (resetPage) {
      this.currentPage = 1;
    }

    this.updatePaginatedEmployees();
  }

  updatePaginatedEmployees(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;

    const endIndex = startIndex + this.itemsPerPage;

    this.viewEmployees = this.filteredEmployees.slice(startIndex, endIndex);

    if (
      this.viewEmployees.length &&
      !this.viewEmployees.some(
        (employee) =>
          this.resolveEmployeeId(employee) ===
          this.resolveEmployeeId(this.selectedEmployee),
      )
    ) {
      this.selectedEmployee = this.viewEmployees[0];
    }

    if (!this.viewEmployees.length) {
      this.selectedEmployee = null;
    }
  }

  onPageChange(page: number): void {
    this.currentPage = page;

    this.updatePaginatedEmployees();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;

    this.currentPage = 1;

    this.updatePaginatedEmployees();
  }

  getEmployeeSuggestion(employee: any): string {
    if (!employee?.is_active) {
      return "This employee is inactive. Review whether they should remain hidden from scheduling and reports.";
    }

    if (!employee?.phone_number) {
      return "Phone number is missing. Add contact details so the salon can reach this employee quickly.";
    }

    if (
      !this.getEmployeeWorkingDays(employee) ||
      this.getEmployeeWorkingDays(employee) === "N/A"
    ) {
      return "Working days are not configured. Add availability to support scheduling and AI booking.";
    }

    if (!employee?.job_title) {
      return "Job title is missing. Add a role so reporting and service coverage are clearer.";
    }

    return "Employee profile looks healthy. Keep availability, services, and contact details updated.";
  }
}
